import { useItemModifierGroups } from 'src/hooks/useItemModifierGroups';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import {
  flattenPagesToModifiers,
  generateModifierFormDefaults,
  mapFieldValueToPrice,
  mapModifierGroupToTableRow,
} from './utils';
import {
  BundleIcon,
  MenuIcon,
  MultiSelect,
  Paper,
  Prompt,
  SearchField,
  SnackbarStatus,
  Spinner,
  Table,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { ItemModifierGroupsResponseBody } from 'src/api/getModifierGroups';
import { optionGroupCols } from './Table';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';

import { updateProductsPricing } from 'src/api/updateProductsPricing/updateProductsPricing';
import {
  BUNDLE_MODIFIER_DESCRIPTION,
  BUNDLE_MODIFIER_FORM_ID,
  BUNDLE_MODIFIER_TITLE,
  BUNDLE_MODIFIER_UPDATE_MSG,
  CREATE_BUNDLE_OR_MODIFIER_FIRST,
  GO_TO_PRODUCTS,
} from './constants';
import styles from '../PriceSchemeOptionGroupOverrides/PriceSchemeOptionGroupOverrides.module.scss';
import { createPortal } from 'react-dom';
import { PricingSaveSummary } from '../PricingSaveSummary/PricingSaveSummary';
import { PAGE_FOOTER_ID } from 'src/constants';
import EmptyState from '../EmptyState';
import { useMenus } from 'src/hooks/useMenus';
import { emptySectionTextFilter } from '../PriceSchemePricingTab/constants';
import { Routes } from 'src/routes';
import { useCancelCheck } from 'src/hooks/useCancelCheck';

const PriceSchemeProductOverridesTab = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const { showSnackbar } = useSnackbar();
  const [searchMenus, setSearchMenus] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue);

  const onNewProducts = useCallback(({ pages }: InfiniteData<ItemModifierGroupsResponseBody>) => {
    const flattenedPages = flattenPagesToModifiers(pages);
    const formValues = generateModifierFormDefaults(flattenedPages);
    reset(formValues, { keepValues: true, keepDirtyValues: true, keepErrors: true });
  }, []);

  const { data: menus } = useMenus();

  const { data, isLoading, queryKey, isFetchingNextPage, fetchStatus, fetchNextPage, hasNextPage } =
    useItemModifierGroups({
      priceSchemeId,
      menus: searchMenus,
      name: debouncedSearchValue,
      options: {
        onSuccess: onNewProducts,
      },
    });

  const pages = data?.pages?.flatMap(({ data }) => data) ?? [];
  const tableData = pages.map(mapModifierGroupToTableRow);
  const modifiers = pages.flatMap((page) => page.modifier_groups).flatMap((p) => p.modifiers) ?? [];
  const defaultValues = generateModifierFormDefaults(modifiers);

  const queryClient = useQueryClient();
  const { control, handleSubmit, reset, setValue, formState } = useForm<FieldValues>({
    defaultValues,
    values: defaultValues,
  });

  const navigate = useNavigate();

  const totalChanges = Object.keys(formState.dirtyFields).length;

  const showMenuFilterNoResults = modifiers?.length === 0 && searchMenus.length > 0;
  const emptyStateText = showMenuFilterNoResults ? emptySectionTextFilter : CREATE_BUNDLE_OR_MODIFIER_FIRST;

  const columns = useMemo(() => optionGroupCols({ control, setValue }), [control, setValue, fetchStatus]);

  const { isCancelCheck, message, handleStay, handleLeave } = useCancelCheck({
    totalChanges,
  });

  const submitValues = async (fieldValues: FieldValues) => {
    try {
      const dirtyKeys = Object.keys(formState.dirtyFields);
      const values = Object.entries(fieldValues).filter(([key]) => dirtyKeys.includes(key));

      const payload = values.map(mapFieldValueToPrice);
      await updateProductsPricing(priceSchemeId, payload);

      queryClient.invalidateQueries({ queryKey });
      reset(fieldValues, { keepValues: false });

      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: BUNDLE_MODIFIER_UPDATE_MSG.SUCCESS,
      });
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : BUNDLE_MODIFIER_UPDATE_MSG.FAILURE,
      });
    }
  };

  if (isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  return (
    <div className={styles['container']}>
      <Prompt
        closePrompt={handleStay}
        open={isCancelCheck}
        buttonHandler={handleLeave}
        buttonText="Leave page"
        backButton
        backButtonText="Review changes"
        promptHeading="Unsaved changes"
        size="small"
      >
        {message}
      </Prompt>
      <div className={styles['search-filter-container']}>
        <SearchField
          key="bundle-modifier-search"
          isDisabled={false}
          placeholder="Search"
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
        />
        {menus?.length > 0 && (
          <MultiSelect
            Icon={MenuIcon}
            label="Menus"
            options={menus?.map((menu, index) => ({ id: menu.menuId, label: menu.name, display_order: index })) || []}
            selected={searchMenus}
            onChange={setSearchMenus}
            disabled={menus?.length === 0}
          />
        )}
      </div>

      <form id={BUNDLE_MODIFIER_FORM_ID} onSubmit={handleSubmit(submitValues)} className={styles['rules-list']}>
        {tableData.length > 0 ? (
          <Table
            className={styles['table-fill-height']}
            data={tableData}
            columns={columns}
            columnHeadersToRightAlign={['adjustmentValue', 'roundingValue', 'actions']}
            getSubRows={(row) => row.modifiers}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        ) : (
          <Paper className={styles['empty-state-paper']}>
            <Typography size="xlarge" weight="semi-bold">
              {BUNDLE_MODIFIER_TITLE}
            </Typography>
            <Typography className={styles['description']}>{BUNDLE_MODIFIER_DESCRIPTION}</Typography>
            <EmptyState
              emptySectionText={emptyStateText}
              {...(!showMenuFilterNoResults && {
                buttonText: GO_TO_PRODUCTS,
                onClick: () => navigate(Routes.PRODUCT_LIBRARY),
              })}
              Icon={BundleIcon}
            />
          </Paper>
        )}
        {totalChanges > 0 &&
          createPortal(
            <PricingSaveSummary
              form={BUNDLE_MODIFIER_FORM_ID}
              reset={reset}
              totalChanges={totalChanges}
              isSubmitting={formState.isSubmitting}
            />,
            document.getElementById(PAGE_FOOTER_ID),
          )}
      </form>
    </div>
  );
};

export default PriceSchemeProductOverridesTab;
